import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { dailyItemService, businessUserService } from "../services/authService";
import DailyItemModal from "./DailyItemModal";
import ConfirmationModal from "./ConfirmationModal";

const DailyItemsManagement = ({ user }) => {
  const [dailyItems, setDailyItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // "active" or "all"

  const fetchDailyItems = useCallback(async () => {
    try {
      setLoading(true);
      const includeStatus = statusFilter === "all" ? "all" : null;
      const itemsData = await dailyItemService.getAllDailyItemsAdmin(
        null,
        null,
        selectedUser || null,
        includeStatus
      );

      // Apply category filter in frontend if selected
      const filteredItems =
        categoryFilter === "all"
          ? itemsData
          : itemsData.filter((item) => item.category === categoryFilter);

      setDailyItems(filteredItems);
    } catch (error) {
      console.error("Error fetching daily items:", error);
      toast.error("Failed to fetch daily items");
    } finally {
      setLoading(false);
    }
  }, [selectedUser, categoryFilter, statusFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      // Fetch users from business-user relationships instead of legacy users
      const usersData =
        await businessUserService.getBusinessUsersForAssignment();
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching business users:", error);
      // Fallback to empty array if no business users are found
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    fetchDailyItems();
    fetchUsers();
  }, [fetchDailyItems, fetchUsers]);

  const handleCreateItem = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteItem = (itemId) => {
    const item = dailyItems.find((item) => item._id === itemId);
    setItemToDelete(item);
    setShowConfirmation(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      await dailyItemService.deleteDailyItem(itemToDelete._id);
      toast.success("Daily item deleted successfully");
      fetchDailyItems();
      setShowConfirmation(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting daily item:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete daily item"
      );
    }
  };

  const cancelDeleteItem = () => {
    setShowConfirmation(false);
    setItemToDelete(null);
  };

  const handleItemSaved = () => {
    setShowModal(false);
    setEditingItem(null);
    fetchDailyItems();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getFrequencyDisplay = (item) => {
    if (item.frequency === "daily") return "Daily";
    if (item.frequency === "weekly") {
      return `Weekly (${item.weeklyDays?.join(", ") || "No days set"})`;
    }
    if (item.frequency === "monthly") {
      return `Monthly (${item.monthlyDate || "No date set"})`;
    }
    return item.frequency;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <h2>📦 Daily Items Management</h2>
          <p>Manage daily delivery items and schedules</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateItem}>
          Add Daily Item
        </button>
      </div>

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "20px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* User Filter */}
        <div>
          <label style={{ marginRight: "10px", fontWeight: "bold" }}>
            Filter by User:
          </label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "180px",
            }}
          >
            <option value="">All Users</option>
            {users.map((user) => (
              <option key={user._id} value={user._id}>
                {user.fullName} ({user.uniqueUserId})
              </option>
            ))}
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <label style={{ marginRight: "10px", fontWeight: "bold" }}>
            Filter by Category:
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "140px",
            }}
          >
            <option value="all">All Categories</option>
            <option value="milk">Milk</option>
            <option value="newspaper">Newspaper</option>
            <option value="groceries">Groceries</option>
            <option value="medicine">Medicine</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label style={{ marginRight: "10px", fontWeight: "bold" }}>
            Assignment Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              width: "160px",
            }}
          >
            <option value="active">Active Items (approved/pending)</option>
            <option value="all">All Items (include denied)</option>
          </select>
        </div>
      </div>

      {/* No Users Notice */}
      {users.length === 0 && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
          }}
        >
          <h4 style={{ color: "#856404", margin: "0 0 10px 0" }}>
            👥 No Users Available
          </h4>
          <p style={{ color: "#856404", margin: "0" }}>
            You haven't added any users to your business yet. Please go to the{" "}
            <strong>User Management</strong> tab to add users by their unique ID
            before creating daily items.
          </p>
        </div>
      )}

      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            backgroundColor: "#e8f4fd",
            border: "1px solid #b3d7ff",
            borderRadius: "8px",
            padding: "15px",
          }}
        >
          <h3 style={{ color: "#0066cc", marginBottom: "10px" }}>
            📋 Daily Items Overview
          </h3>
          <p style={{ color: "#0066cc", margin: "0" }}>
            Total Active Items:{" "}
            {dailyItems.filter((item) => item.isActive).length} | Total Items:{" "}
            {dailyItems.length}
            {(selectedUser ||
              categoryFilter !== "all" ||
              statusFilter !== "active") &&
              " (filtered by: "}
            {selectedUser && "user"}
            {selectedUser &&
              (categoryFilter !== "all" || statusFilter !== "active") &&
              " & "}
            {categoryFilter !== "all" && `category: ${categoryFilter}`}
            {categoryFilter !== "all" && statusFilter !== "active" && " & "}
            {statusFilter !== "active" && "status: all"}
            {(selectedUser ||
              categoryFilter !== "all" ||
              statusFilter !== "active") &&
              ")"}
          </p>
        </div>
      </div>

      {dailyItems.length === 0 ? (
        <div className="text-center" style={{ padding: "40px" }}>
          <p>No daily items found. Create your first delivery schedule!</p>
        </div>
      ) : (
        <div
          style={{
            maxHeight: "600px",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <table className="table" style={{ marginBottom: "0" }}>
            <thead
              style={{
                position: "sticky",
                top: "0",
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
                zIndex: 10,
              }}
            >
              <tr>
                <th>Title</th>
                <th>Assigned To</th>
                <th>Category</th>
                <th>Frequency</th>
                <th>Delivery Time</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dailyItems.map((item) => (
                <tr key={item._id}>
                  <td>
                    <div>
                      <strong>{item.title}</strong>
                      {item.description && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {item.description}
                        </div>
                      )}
                      {item.quantity && (
                        <div style={{ fontSize: "11px", color: "#888" }}>
                          Qty: {item.quantity}
                          {item.price > 0 && ` | ₹${item.price}`}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>{item.assignedTo?.username}</strong>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {item.assignedTo?.email}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className="btn btn-secondary"
                      style={{ padding: "2px 6px", fontSize: "11px" }}
                    >
                      {item.category.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: "12px" }}>
                      {getFrequencyDisplay(item)}
                    </div>
                  </td>
                  <td>{item.deliveryTime}</td>
                  <td>{formatDate(item.startDate)}</td>
                  <td>
                    {item.isIndefinite ? (
                      <span
                        style={{
                          color: "#28a745",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        ∞ Indefinite
                      </span>
                    ) : (
                      formatDate(item.endDate)
                    )}
                  </td>
                  <td>
                    <div>
                      <span
                        style={{
                          color: item.isActive ? "#28a745" : "#dc3545",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                      {/* Assignment Status Badge */}
                      {item.assignmentStatus && (
                        <div style={{ marginTop: "4px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "10px",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              color: "white",
                              backgroundColor:
                                item.assignmentStatus === "approved"
                                  ? "#28a745"
                                  : item.assignmentStatus === "denied"
                                  ? "#dc3545"
                                  : "#ffc107",
                            }}
                          >
                            {item.assignmentStatus === "approved" &&
                              "✅ Approved"}
                            {item.assignmentStatus === "denied" && "❌ Denied"}
                            {item.assignmentStatus === "pending" &&
                              "⏳ Pending"}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-secondary"
                        style={{ padding: "4px 8px", fontSize: "11px" }}
                        onClick={() => handleEditItem(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: "4px 8px", fontSize: "11px" }}
                        onClick={() => handleDeleteItem(item._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Summary */}
      {dailyItems.length > 0 && (
        <div
          style={{
            marginTop: "10px",
            padding: "8px 12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#6c757d",
            textAlign: "center",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            Showing {dailyItems.length} daily item
            {dailyItems.length !== 1 ? "s" : ""}(
            {dailyItems.filter((item) => item.isActive).length} active)
          </span>
          {dailyItems.length > 10 && (
            <span style={{ fontSize: "12px" }}>
              📜 Scroll within the table above to see all items
            </span>
          )}
        </div>
      )}

      {showModal && (
        <DailyItemModal
          item={editingItem}
          users={users}
          onClose={() => setShowModal(false)}
          onSave={handleItemSaved}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={cancelDeleteItem}
        onConfirm={confirmDeleteItem}
        title="Delete Daily Item"
        message={
          itemToDelete
            ? `Are you sure you want to delete "${itemToDelete.title}"?\n\nThis will also delete all associated delivery logs.\n\nThis action cannot be undone.`
            : "Are you sure you want to delete this daily item?"
        }
        confirmText="Delete Item"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default DailyItemsManagement;
